import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { authClient } from "../api/authClient";
import { refreshApiV1AuthRefreshPost } from "../api/generated/sdk.gen";
import { loginRequest } from "../authConfig";
import { msalInstance as appMsalInstance } from "../index";
import {
  getAppTokenLifetimeMs,
  getTokenUserKey,
  hasValidAppToken,
  isAppToken,
} from "./appToken";
import { clearRecordingRecoveryData } from "./clearRecordingRecovery";
import {
  clearAuthRelatedLocalStorage,
  deleteAccessTokenCookie,
  getAccessTokenCookie,
  setAccessTokenCookie,
  wasExplicitlyLoggedOut,
} from "./cookies";
import {
  blockRecentSearchWrites,
  clearRecentSearches,
} from "./recentSearchesStorage";
import { authDebug, authDebugError } from "./safeAuthLog";
import { runSessionTeardown } from "./sessionTeardown";

// Use the already initialized MSAL instance from index.js
const getMsalInstance = () => {
  return appMsalInstance;
};

/**
 * Token refresh utility using MSAL
 * Let MSAL handle refresh tokens - we just ensure we can get a valid token
 * @deprecated This function is kept for backward compatibility but should not be used.
 * MSAL handles token refresh automatically via acquireTokenSilent.
 */
export const refreshTokenIfNeeded = async (): Promise<boolean> => {
  try {
    authDebug("refreshTokenIfNeeded: attempting to acquire a fresh token");

    // Get the initialized MSAL instance
    const instance = getMsalInstance();

    // First, handle any pending redirect promise (user might be returning from login)
    try {
      const redirectResponse = await instance.handleRedirectPromise();
      if (redirectResponse?.account) {
        instance.setActiveAccount(redirectResponse.account);
        if (redirectResponse.accessToken) {
          setAccessTokenCookie(redirectResponse.accessToken);
          return true;
        }
      }
    } catch (redirectError) {
      // Ignore redirect errors, continue with normal flow
      authDebugError(
        "refreshTokenIfNeeded: no redirect (or redirect error)",
        redirectError
      );
    }

    // Make sure the instance is initialized before using it
    if (!instance.getActiveAccount()) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        authDebug("refreshTokenIfNeeded: setting active account from cache");
        instance.setActiveAccount(accounts[0]);
      } else {
        authDebug("refreshTokenIfNeeded: no accounts found");
        // Don't trigger logout here - just return false gracefully
        // The calling code should handle this appropriately
        return false;
      }
    }

    const account = instance.getActiveAccount();
    if (!account) {
      authDebug("refreshTokenIfNeeded: no active account");
      // Don't trigger logout here - just return false gracefully
      return false;
    }

    // Try to silently acquire a new token - MSAL will use refresh token if needed
    try {
      authDebug("refreshTokenIfNeeded: acquiring token silently via MSAL");
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
        scopes: loginRequest.scopes.filter(
          (scope): scope is string => scope !== undefined
        ),
      });

      if (response?.accessToken) {
        // Save the token to cookies so apiClient can use it
        setAccessTokenCookie(response.accessToken);
        return true;
      } else {
        authDebug("refreshTokenIfNeeded: no access token in response");
        return false;
      }
    } catch (refreshError) {
      authDebugError(
        "refreshTokenIfNeeded: token silent refresh failed",
        refreshError
      );

      // Check if this is an interaction required error
      if (refreshError instanceof InteractionRequiredAuthError) {
        authDebug("refreshTokenIfNeeded: interaction required");
        return false;
      }

      return false;
    }
  } catch (error) {
    authDebugError("refreshTokenIfNeeded: unexpected failure", error);
    return false;
  }
};

/**
 * Exchange the httpOnly refresh-token cookie for a new access token — the
 * external/OTP equivalent of refreshTokenIfNeeded's MSAL acquireTokenSilent
 * (MDRS-556). The access token is in-memory only now, so this is what lets a
 * new tab, a bookmarked link, or a reopened browser pick the session back up
 * without the token ever having touched localStorage/sessionStorage.
 *
 * Deliberately routed through authClient, not apiClient: apiClient's own
 * fetch wrapper calls this function when a request 401s, so routing this
 * call back through apiClient would recurse into that same 401 handler on a
 * failed refresh.
 *
 * Single-flight: concurrent callers (several requests 401ing together on page
 * load or token expiry) share one request. The refresh cookie is single-use,
 * so sending it more than once would rotate it repeatedly and can trip the
 * backend's reuse detection, which revokes the whole session.
 */
let inflightAppRefresh: Promise<boolean> | null = null;

export const refreshAppTokenIfNeeded = (): Promise<boolean> => {
  inflightAppRefresh ??= (async () => {
    try {
      const response = await refreshApiV1AuthRefreshPost({ client: authClient });
      const newAccessToken = response.data?.access_token;
      if (!newAccessToken) return false;

      if (wasExplicitlyLoggedOut()) return false;

      const heldToken = getAccessTokenCookie();
      if (
        heldToken &&
        isAppToken(heldToken) &&
        getTokenUserKey(heldToken) !== getTokenUserKey(newAccessToken)
      ) {
        await logoutUser(false, "Refresh returned a different user");
        return false;
      }
      storeAppToken(newAccessToken);
      return true;
    } catch (error) {
      authDebugError("refreshAppTokenIfNeeded: refresh failed", error);
      return false;
    } finally {
      inflightAppRefresh = null;
    }
  })();
  return inflightAppRefresh;
};

/** Renew an app token once this share of its lifetime has passed. */
const APP_TOKEN_REFRESH_AT = 0.8;
/** Wait before trying again after a renewal that failed. */
const APP_TOKEN_REFRESH_RETRY_MS = 30_000;

let appTokenRefreshTimer: ReturnType<typeof setTimeout> | undefined;

const scheduleAppTokenRefresh = (token: string, delayMs: number): void => {
  clearTimeout(appTokenRefreshTimer);
  appTokenRefreshTimer = setTimeout(async () => {
    // Replaced since, or cleared by a logout here or in another tab.
    if (getAccessTokenCookie() !== token || wasExplicitlyLoggedOut()) return;
    // Success stores the new token, which schedules its own renewal.
    if (await refreshAppTokenIfNeeded()) return;
    // A failed renewal isn't a logout (the next request's 401 decides that):
    // keep trying while the token still works.
    if (hasValidAppToken()) {
      scheduleAppTokenRefresh(token, APP_TOKEN_REFRESH_RETRY_MS);
    }
  }, delayMs);
};

/**
 * Store an app-issued (email-code) access token and renew it before it
 * expires (MDRS-556).
 *
 * Waiting for a 401 isn't enough on its own: a tab that makes no requests
 * (bundle polling stands down once useIsSessionActive sees the token expire)
 * would never trigger one, and would sit signed-out-looking until the user
 * clicked something.
 */
export const storeAppToken = (token: string): void => {
  setAccessTokenCookie(token);
  const lifetimeMs = getAppTokenLifetimeMs(token);
  if (lifetimeMs) {
    scheduleAppTokenRefresh(token, lifetimeMs * APP_TOKEN_REFRESH_AT);
  }
};

/**
 * Check if user has a valid authentication state
 * Uses MSAL account state instead of cookies
 */
export const isUserAuthenticated = (): boolean => {
  // External (email-code) users have no MSAL account — a valid app-issued token
  // is their session until it expires (storeAppToken renews it before then).
  if (hasValidAppToken()) {
    return true;
  }
  const instance = getMsalInstance();
  const account = instance.getActiveAccount();
  if (!account) {
    const accounts = instance.getAllAccounts();
    return accounts.length > 0;
  }
  return true;
};

/**
 * Enhanced logout function that handles authentication failures
 * @param redirect - Whether to redirect after logout (defaults to false)
 * @param reason - Reason for logout (for logging purposes)
 * @returns {Promise<void>}
 */
export const logoutUser = async (
  redirect = false,
  reason = "User initiated"
): Promise<void> => {
  try {
    authDebug(`logoutUser: logging out (${reason})`);

    // Synchronous, ahead of the await below: some callers redirect without
    // awaiting logoutUser (MDRS-558).
    blockRecentSearchWrites();
    clearRecentSearches();
    // Before any redirect, so interrupted-recording PHI can't outlive the session.
    await clearRecordingRecoveryData();
    // Cached case data and pop-out windows go too (MDRS-486) — an internal
    // user's redirect below is cancelled, so this tab may stay mounted.
    runSessionTeardown();

    // Clear MSAL state
    const instance = getMsalInstance();
    // External (email-code) users have no Microsoft session to end — decide
    // before we clear the token so we never send them to the Microsoft logout.
    const isExternal = hasValidAppToken();
    if (instance) {
      // Reset active account (MSAL doesn't have a direct removeAccount method)
      instance.setActiveAccount(null);

      // Only the MSAL (internal) path uses logoutRedirect.
      if (redirect && !isExternal) {
        authDebug("logoutUser: redirecting to logout page");
        try {
          await instance.logoutRedirect({
            onRedirectNavigate: () => false,
            postLogoutRedirectUri: window.location.origin + "/",
          });
        } catch (logoutError) {
          authDebugError(
            "logoutUser: MSAL logout redirect failed; forcing redirect",
            logoutError
          );
          window.location.href = "/";
        }
      }
    }

    // Clear in-memory access token (and any legacy token cookies)
    deleteAccessTokenCookie();

    // Clear any additional auth-related storage
    try {
      sessionStorage.clear();
      clearAuthRelatedLocalStorage();
    } catch (storageError) {
      authDebugError("logoutUser: error clearing storage", storageError);
    }

    // External users skip MSAL's redirect above, so send them to landing here.
    if (redirect && isExternal) {
      window.location.href = "/";
    }

    authDebug("logoutUser: completed");
  } catch (error) {
    authDebugError("logoutUser: failed", error);

    // Force redirect if explicitly requested and MSAL logout failed
    if (redirect) {
      authDebug("logoutUser: forcing redirect due to failure");
      window.location.href = "/";
    }
  }
};

/**
 * Handle authentication errors globally
 * This function should be called when authentication fails
 * @param error - The authentication error
 * @param context - Context where the error occurred (for logging)
 */
export const handleAuthenticationError = async (
  error: unknown,
  context = "Unknown"
): Promise<void> => {
  authDebugError(`handleAuthenticationError (${context})`, error);

  // Check if this is a token expiration error
  const message = error instanceof Error ? error.message : undefined;
  const isTokenExpired =
    message?.includes("401") ||
    message?.includes("expired") ||
    message?.includes("Authentication failed");

  if (isTokenExpired) {
    authDebug("handleAuthenticationError: token expired; logging out");
    // Since callApi now handles 401s and token refresh via MSAL,
    // if we get here, the user likely needs to log in again
    await logoutUser(true, `Authentication error in ${context}`);
  } else {
    authDebug("handleAuthenticationError: auth error; logging out");
    await logoutUser(true, `Authentication error in ${context}`);
  }
};

/**
 * Setup automatic token refresh with enhanced error handling
 * @param intervalMinutes - How often to check for token refresh (default: 15 minutes)
 * @returns {number} - Interval ID for cleanup
 * @deprecated MSAL handles token refresh automatically. This is kept for backward compatibility.
 */
export const setupTokenRefresh = (_intervalMinutes = 15) => {
  authDebug("setupTokenRefresh: MSAL manages refresh; no-op");

  // MSAL handles token refresh automatically via acquireTokenSilent
  // We don't need to set up periodic checks anymore
  // Return a dummy interval ID for cleanup compatibility
  return 0;
};

type WindowWithTokenRefresh = Window & {
  __tokenRefreshIntervalId?: ReturnType<typeof setInterval>;
};

/**
 * Cleanup automatic token refresh
 */
export const cleanupTokenRefresh = () => {
  const windowWithTokenRefresh = window as WindowWithTokenRefresh;
  const intervalId = windowWithTokenRefresh.__tokenRefreshIntervalId;
  if (intervalId) {
    authDebug("cleanupTokenRefresh: clearing interval");
    clearInterval(intervalId);
    delete windowWithTokenRefresh.__tokenRefreshIntervalId;
  }
};

/**
 * Global error handler for authentication failures
 * This should be called from error boundaries or global error handlers
 *
 * NOTE: We no longer intercept fetch calls globally as callApi handles 401s.
 * This prevents conflicts and lets MSAL manage tokens properly.
 */
export const setupGlobalAuthErrorHandler = () => {
  // Handle unhandled promise rejections related to authentication
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;

    // Check if this is an authentication-related error
    if (
      error?.message?.includes("401") ||
      error?.message?.includes("Authentication failed") ||
      error?.message?.includes("expired")
    ) {
      authDebugError("setupGlobalAuthErrorHandler: unhandled auth error", error);

      // Prevent the default error handling
      event.preventDefault();

      // Handle the authentication error
      handleAuthenticationError(error, "Global Error Handler");
    }
  });

  authDebug("setupGlobalAuthErrorHandler: installed");
};
